import type { Choice } from './choices';
export type { Choice, LegacyChoice, ChoiceFormat } from './choices';
export {
  isChoice,
  normalizeChoice,
  normalizeChoicePayload,
  legacyChoicePayload,
} from './choices';
export const CHAIN_ID = 4663;
export type {
  OddsPreviewRequest,
  OddsPreviewResponse,
  OddsPreviewState,
  LiveOdds,
  LockedOdds,
  OddsSelection,
} from './odds';
export { LIVE_ODDS_MODEL, DEFAULT_ODDS_AMOUNT } from './odds';
export const PREDICTION_WINDOW_MS = 20_000;
export const ROUND_DURATION_MS = 5 * 60_000;
// Persisted round deadlines bound recovery across worker restarts.
export const ROUND_EVIDENCE_GRACE_MS = 15 * 60_000;
export type Address = `0x${string}`;
export type RuleVersion = 'pair-v1' | 'usd-v2';
export type InitialPriceBasis = 'graduation-block-v1' | 'pool-initialize-v1';
export interface InitialPriceEvidence {
  priceInPair: string;
  sqrtPriceX96: string;
  blockNumber: number;
  blockHash: Address;
  transactionHash: Address;
  logIndex: number;
}
export interface FinalPriceEvidence {
  blockNumber: number;
  blockHash: Address;
  timestamp: number;
  priceInPair: string;
  priceUsd: string | null;
  usdQuoteId: number | null;
}
export type UsdStatus = 'ready' | 'missing' | 'stale' | 'halted';
// Decimal strings preserve provider precision. Legacy pair-denominated fields stay unchanged.
export interface UsdValuation {
  currentPriceUsd?: string | null;
  currentMCUsd?: string | null;
  capUsd?: string | null;
  usdStatus?: UsdStatus;
  usdAsOf?: number | null;
  usdSource?: string | null;
  usdQuoteId?: number | null;
  usdValidUntil?: number | null;
}
export const tokenId = (address: string) =>
  `${CHAIN_ID}:${address.toLowerCase()}`;
export const isAddress = (value: string): value is Address =>
  /^0x[a-fA-F0-9]{40}$/.test(value);
export const isTokenId = (value: unknown): value is string =>
  typeof value === 'string' && /^4663:0x[a-f0-9]{40}$/.test(value);

// Facts owned by the blockchain application. Prices and market caps use the pair currency.
export interface Token extends UsdValuation {
  id: string;
  address: Address;
  chainId: number;
  name: string;
  symbol: string;
  decimals: number | null;
  quoteDecimals: number | null;
  quoteSymbol: string | null;
  totalSupply: string | null;
  logo: string | null;
  description: string | null;
  socials: {
    twitter: string;
    telegram: string;
    discord: string;
    website: string;
    farcaster: string;
  } | null;
  metadataComplete: boolean;
  start: number;
  graduationBlock: number;
  graduationTx: Address;
  graduationBlockHash: Address;
  initialPriceBasis?: InitialPriceBasis;
  initialPriceEvidence?: InitialPriceEvidence | null;
  finalPriceEvidence?: FinalPriceEvidence | null;
  curveAddress: Address;
  pairToken: Address;
  deployer: Address;
  poolId: Address;
  poolManager: Address;
  hook: Address;
  poolFee: number;
  tickSpacing: number;
  phase: number;
  currentPrice: number | null;
  cap: number | null;
  currentMC: number | null;
  tradeCount: number | null;
  observedTradeCount: number;
  lastTradeAt: number | null;
  activityCursor: number;
  activityComplete: boolean;
  roundTradeCount: number;
  priceUpdatedAt: number | null;
  priceError?: string | null;
  quoteAttemptedAt?: number;
  metadataAttemptedAt?: number;
  logoCheckedAt?: number;
  metricError?: string | null;
  currentPriceExact?: string | null;
  priceBlockNumber?: number;
  priceLogIndex?: number;
  canonical?: boolean;
  ruleVersion?: RuleVersion;
}
export interface Round extends UsdValuation {
  cancelled?: boolean;
  odds?: import('./odds').LiveOdds | null;
  lockedOdds?: import('./odds').LockedOdds;
  id: string;
  address: string;
  name: string;
  symbol: string;
  start: number;
  predictionClosesAt: number;
  settlesAt: number;
  cap: number | null;
  currentMC: number | null;
  currentPrice: number | null;
  p0: number | null;
  pt: number | null;
  outcome: Choice | null;
  chosenAt?: number;
  choice?: Choice;
  split: number | null;
  votes?: number;
  // Calls for RUN; the rest of `votes` called DIE. Both sides are counted,
  // never derived from the rounded percentage.
  survivesVotes?: number;
  logo: string | null;
  // Optional for compatibility with older snapshots and locally saved rounds.
  socials?: Token['socials'];
  pairToken: string;
  quoteSymbol: string | null;
  tradeCount: number | null;
  activityComplete: boolean;
  activityCursor: number;
  graduationBlock: number;
  lastTradeAt: number | null;
  initialPriceBasis?: InitialPriceBasis;
  initialPriceEvidence?: InitialPriceEvidence | null;
  priceUpdatedAt: number | null;
  finalPriceEvidence?: FinalPriceEvidence | null;
  priceError?: string | null;
  history: number[];
  historyUsd?: string[];
  p0Usd?: string | null;
  ptUsd?: string | null;
  ruleVersion?: RuleVersion;
  canonical?: boolean;
}
export interface Leader {
  id: string;
  name: string;
  avatar: string;
  score: number;
  you: boolean;
}
export interface ArenaSnapshot {
  mode: 'live';
  sequence?: number;
  rounds: Round[];
  predictions: Round[];
  ranking: Leader[];
  serverTime: number;
  lastSync: number | null;
  syncError: string;
  indexedBlock: number | null;
  headBlock: number | null;
  marketStatus?: string;
  marketUpdatedAt?: number | null;
}
export interface MarketUpdate {
  type: 'market';
  sequence: number;
  serverTime: number;
  rounds: Round[];
  removedIds?: string[];
  marketStatus: string;
  marketUpdatedAt: number | null;
}
export type ArenaEvent = ArenaSnapshot | MarketUpdate;
export type ArenaResponse = ArenaSnapshot | { mode: 'mock' };
export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unexpected error';
}
export type {
  PointsBalance,
  PointsEntry,
  PointsHistory,
  PointsRewards,
} from './points';
export { MIN_STAKE, MAX_POINTS, parseStake, clampStake } from './stake';
export type {
  LeaderboardReward,
  LeaderboardEntry,
  LeaderboardPeriod,
  LeaderboardResponse,
} from './leaderboard';
